/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/Annotations/Annotations" />

import * as React from "react";
import { css, useService } from "VSS/Platform/Layout";
import { BoardAnnotationsIdentifiers } from "Wit/BoardsHub/Common/AnnotationUtils";
import { IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { isChecklistAnnotation } from "Wit/BoardsHub/KanbanBoard/AnnotationService";
import { IKanbanBoardService } from "../../IKanbanBoardService";
import { ChecklistAnnotation } from "./ChecklistAnnotation";
import { AnnotationComponent } from "./Common";
import { GitHubAnnotation } from "./GitHubAnnotation";
import { TestAnnotation } from "./TestAnnotation";
import { hexToRgb, isDark } from "VSSUI/Utilities/Color";
import { useTfsTeamData } from "Tfs/Platform/TfsComponent";

/**
 * Renders the block of annotations at the bottom of a card
 */
export function Annotations({ item }: { item: IObservableBoardsWorkItem }) {
    const [expanded, setExpanded] = React.useState<string | undefined>(item.expandedAnnotation);
    const onClick = (name: string) => {
        const updatedAnnotation = expanded === name ? undefined : name;
        setExpanded(updatedAnnotation);
        item.expandedAnnotation = updatedAnnotation;
    };

    const boardService = useService<IKanbanBoardService>("IKanbanBoardService");
    const teamId = useTfsTeamData()?.id;

    const onModifyItems = () => {
        boardService.reloadAnnotaionData([item.value.id], teamId ?? '');
    };

    const annotationSettings = boardService.boardSettingsData.value?.annotationSettings?.filter(as => as.isEnabled);
    const workItemTypeColorAndIconsMap = boardService.boardLayout.value?.workItemTypeColorAndIconsMap;
    const personIdentities = boardService.boardSettingsData.value?.personIdentities;

    const textColor: string | undefined = item.value.backgroundColor ? 
        isDark(hexToRgb(item.value.backgroundColor)) ? css("dark") : css("light") 
        : undefined;

    React.useEffect(() => {
        setExpanded(item.expandedAnnotation);
    }, [item.expandedAnnotation]);

    if (!annotationSettings || annotationSettings.length === 0 || !workItemTypeColorAndIconsMap) {
        return null;
    }

    const annotations = item.value.annotations;
    if (!annotations && !item.expandedAnnotation) {
        return null;
    }

    return (
        <div className="annotations flex-row flex-wrap margin-top-8 rhythm-horizontal-8">
            {annotationSettings.map(a => {
                const Component = annotationComponentMap[a.id] ?? (isChecklistAnnotation(a.id) ? ChecklistAnnotation : undefined);
                if (Component) {
                    return (
                        <Component
                            key={a.name}
                            annotations={annotations}
                            expanded={a.name === expanded}
                            item={item}
                            name={a.name}
                            onClick={onClick}
                            workItemTypeColorAndIconsMap={workItemTypeColorAndIconsMap}
                            itemsColor={textColor}
                            personIdentities={personIdentities}
                            onModifyItems={onModifyItems}
                        />
                    );
                } else {
                    return null;
                }
            })}
        </div>
    );
}

const annotationComponentMap: { [key: string]: AnnotationComponent } = {
    [BoardAnnotationsIdentifiers.GitHubLinksAnnotation]: GitHubAnnotation,
    [BoardAnnotationsIdentifiers.ChecklistAnnotation]: ChecklistAnnotation,
    [BoardAnnotationsIdentifiers.TestAnnotation]: TestAnnotation
};
